/**
 * The statistical categories the site reports, and how each figure is written.
 *
 * @remarks
 * Shared by the Stats page's leaderboards and by a player profile's own
 * figures, so the two cannot drift into labelling or rounding the same
 * statistic differently.
 *
 * ## Two sets of categories
 *
 * {@link STAT_CATEGORIES} describes the *league members* — the people who play
 * in RTL, called "players" everywhere on the site. {@link ROSTER_STAT_CATEGORIES}
 * describes the *in-game players*, the NES athletes on the Tecmo rosters, and
 * is used only by the second section of the Stats page. They are separate
 * arrays over separate row types on purpose: the two sections rank different
 * subjects and share nothing but their withheld statistics, described below.
 *
 * ## Why Scoring leads
 *
 * Every category but Scoring is built from a save state's box score, which the
 * league only began collecting in season 33. Points come from the final score,
 * which every official game has, so Scoring is the only tab that means anything
 * for the Excel-imported seasons — hence first in the array, and first on the
 * page.
 *
 * ## Extra team counters (season 34 on)
 *
 * Time of possession, takeaways, and luck score come from counters the v5 ROM
 * started writing in season 34. The tabs stay on every season; the boards are
 * empty until someone has a season-34+ game with those counters. FG blocks
 * count in luck at the fumble weight; they are not takeaways. Injuries do
 * not count in luck.
 *
 * In-game player tackles are on the Defense boards from season 34 on, when
 * the v5 ROM started writing eleven defender slots. Season 33 stays at zero.
 */

import { EXTRA_STATS_FROM_SEASON, formatPossession } from './teamBoxScore'
import type { CoachStats, RosterPlayerStats, RosterStatLine } from '../types'

/** Signed net, matching Prophet: `+4`, `0`, `-2`. */
function formatSignedStat(value: number): string {
  return value > 0 ? `+${value.toLocaleString()}` : value.toLocaleString()
}

/** One figure within a category. */
export interface StatColumn {
  key: keyof CoachStats
  /** Column heading, kept short enough for a leaderboard header. */
  label: string
  /**
   * Full wording, shown on hover where {@link StatColumn.label} is abbreviated.
   *
   * @remarks
   * Headers do not wrap, so a long label widens the whole table until it
   * scrolls sideways. Abbreviating and putting the full phrase here keeps the
   * table on screen without leaving the reader to guess what a column means.
   */
  hint?: string
  /** Rendering for a figure that is not a plain count, e.g. a percentage. */
  format?: (value: number) => string
}

export interface StatCategory {
  slug: string
  title: string
  description: string
  columns: StatColumn[]
  /** Column the leaderboard sorts by on first render. */
  defaultSort: keyof CoachStats
  /**
   * Which way {@link StatCategory.defaultSort} opens.
   *
   * @remarks
   * Defaults to descending, which is right for every column where more is
   * better. Defense is the exception: it opens on yards allowed per game, and
   * the best defense there is the smallest number, so descending would put the
   * worst team on top and read as a leaderboard of failure.
   */
  defaultSortDirection?: 'asc' | 'desc'
  /**
   * Whether the category's figures come from a parsed box score, and so read
   * zero for a spreadsheet-imported season. Scoring is the exception: points
   * come from the final score, which every game on record carries.
   */
  requiresBoxScore: boolean
  /**
   * Season the underlying counters start. The tab still shows earlier; the
   * board is empty until someone has a game from this year on. Possession,
   * takeaways, and luck use {@link EXTRA_STATS_FROM_SEASON}.
   */
  sinceSeason?: number
  /**
   * Drop rows with no extra-counter games. Those boards would otherwise fill
   * with career records and a column of zeros for anyone who has not played
   * since season 34.
   */
  requiresExtraStats?: boolean
}

export const STAT_CATEGORIES: StatCategory[] = [
  {
    slug: 'scoring',
    title: 'Scoring',
    description: 'Points scored and allowed, the one category recorded for every season.',
    defaultSort: 'pointsFor',
    requiresBoxScore: false,
    columns: [
      { key: 'gamesPlayed', label: 'GP' },
      { key: 'pointsFor', label: 'PF' },
      { key: 'pointsForPerGame', label: 'PF/G', format: value => value.toFixed(1) },
      { key: 'pointsAgainst', label: 'PA' },
      { key: 'pointsAgainstPerGame', label: 'PA/G', format: value => value.toFixed(1) },
    ],
  },
  {
    slug: 'offense',
    title: 'Offense',
    description: 'Total yardage and scoring output per player.',
    defaultSort: 'totalYards',
    requiresBoxScore: true,
    columns: [
      { key: 'gamesPlayed', label: 'GP' },
      { key: 'totalYards', label: 'Total yds' },
      { key: 'yardsPerGame', label: 'Yds/G' },
      { key: 'passingYards', label: 'Pass yds' },
      { key: 'rushingYards', label: 'Rush yds' },
      { key: 'pointsFor', label: 'PF' },
      { key: 'firstDowns', label: '1st downs' },
    ],
  },
  {
    slug: 'passing',
    title: 'Passing',
    description: 'Attempts, efficiency, and passer rating.',
    defaultSort: 'passingYards',
    requiresBoxScore: true,
    columns: [
      { key: 'passingAttempts', label: 'Att' },
      { key: 'passingCompletions', label: 'Cmp' },
      { key: 'completionPercentage', label: 'Cmp%', format: value => `${value.toFixed(1)}%` },
      { key: 'passingYards', label: 'Yds' },
      { key: 'passingTouchdowns', label: 'TD' },
      { key: 'interceptionsThrown', label: 'INT' },
      { key: 'passerRating', label: 'Rating', format: value => value.toFixed(1) },
    ],
  },
  {
    slug: 'rushing',
    title: 'Rushing',
    description: 'Ground game volume and efficiency.',
    defaultSort: 'rushingYards',
    requiresBoxScore: true,
    columns: [
      { key: 'rushingAttempts', label: 'Att' },
      { key: 'rushingYards', label: 'Yds' },
      { key: 'yardsPerCarry', label: 'Y/C', format: value => value.toFixed(2) },
      { key: 'rushingTouchdowns', label: 'TD' },
    ],
  },
  {
    slug: 'defense',
    title: 'Defense',
    description:
      'Yards surrendered per game, tackles, pressure, and takeaways. Fewest yards leads. Tackles are from season 34 on.',
    defaultSort: 'yardsAllowedPerGame',
    defaultSortDirection: 'asc',
    requiresBoxScore: true,
    columns: [
      { key: 'tackles', label: 'Tackles', hint: 'From season 34 on' },
      { key: 'sacks', label: 'Sacks' },
      { key: 'interceptionsMade', label: 'INT' },
      { key: 'passingYardsAllowed', label: 'Pass yds', hint: 'Total passing yards allowed' },
      { key: 'rushingYardsAllowed', label: 'Rush yds', hint: 'Total rushing yards allowed' },
      {
        key: 'passingYardsAllowedPerGame',
        label: 'Pass/G',
        hint: 'Passing yards allowed per game',
        format: v => v.toFixed(1),
      },
      {
        key: 'rushingYardsAllowedPerGame',
        label: 'Rush/G',
        hint: 'Rushing yards allowed per game',
        format: v => v.toFixed(1),
      },
      {
        key: 'yardsAllowedPerGame',
        label: 'Yds/G',
        hint: 'Total yards allowed per game — fewest is best',
        format: v => v.toFixed(1),
      },
    ],
  },
  {
    slug: 'kicking',
    title: 'Kicking',
    description: 'Field goals made and attempted.',
    defaultSort: 'fieldGoalsMade',
    requiresBoxScore: true,
    columns: [
      { key: 'fieldGoalsMade', label: 'FGM' },
      { key: 'fieldGoalAttempts', label: 'FGA' },
    ],
  },
  {
    slug: 'possession',
    title: 'TOP',
    description:
      'Time of possession from season 34 on. Totals and averages are both in minutes and seconds.',
    defaultSort: 'averageTimeOfPossessionSeconds',
    requiresBoxScore: true,
    requiresExtraStats: true,
    sinceSeason: EXTRA_STATS_FROM_SEASON,
    columns: [
      {
        key: 'gamesWithExtraStats',
        label: 'GP',
        hint: 'Games from season 34 on with extra team counters',
      },
      {
        key: 'totalTimeOfPossessionSeconds',
        label: 'Total TOP',
        format: formatPossession,
      },
      {
        key: 'opponentTimeOfPossessionSeconds',
        label: 'Opp TOP',
        hint: 'Opponent total time of possession',
        format: formatPossession,
      },
      {
        key: 'averageTimeOfPossessionSeconds',
        label: 'Avg TOP',
        format: formatPossession,
      },
      {
        key: 'opponentAverageTimeOfPossessionSeconds',
        label: 'Opp Avg',
        hint: 'Opponent average time of possession',
        format: formatPossession,
      },
    ],
  },
  {
    slug: 'takeaways',
    title: 'Takeaways',
    description:
      'Net fumbles recovered, interceptions made, and 4th-down stops from season 34 on.',
    defaultSort: 'netTakeaways',
    requiresBoxScore: true,
    requiresExtraStats: true,
    sinceSeason: EXTRA_STATS_FROM_SEASON,
    columns: [
      {
        key: 'gamesWithExtraStats',
        label: 'GP',
        hint: 'Games from season 34 on with extra team counters',
      },
      {
        key: 'netFumbles',
        label: 'Net Fum',
        hint: 'Opponent fumbles lost minus own fumbles lost',
        format: formatSignedStat,
      },
      {
        key: 'netInterceptions',
        label: 'Net INT',
        hint: 'Interceptions made minus interceptions thrown',
        format: formatSignedStat,
      },
      {
        key: 'netFourthDownStops',
        label: 'Net 4th',
        hint: 'Own 4th-down stops minus opponent 4th-down stops',
        format: formatSignedStat,
      },
      {
        key: 'netTakeaways',
        label: 'Net TO',
        hint: 'Net fumbles + net interceptions + net 4th-down stops',
        format: formatSignedStat,
      },
    ],
  },
  {
    slug: 'luck-score',
    title: 'Luck Score',
    description:
      'Net fumbles are worth ±100 each, net FG blocks ±100, and each net tackle subtracts 2. Injuries do not count.',
    defaultSort: 'netLuckScore',
    requiresBoxScore: true,
    requiresExtraStats: true,
    sinceSeason: EXTRA_STATS_FROM_SEASON,
    columns: [
      {
        key: 'gamesWithExtraStats',
        label: 'GP',
        hint: 'Games from season 34 on with extra team counters',
      },
      {
        key: 'netFumbles',
        label: 'Net Fum',
        hint: 'Opponent fumbles lost minus own fumbles lost. ±100 in luck score.',
        format: formatSignedStat,
      },
      {
        key: 'netTackles',
        label: 'Net Tkl',
        hint: 'Own tackles minus opponent tackles. Each net tackle subtracts 2 luck points.',
        format: formatSignedStat,
      },
      {
        key: 'netFieldGoalBlocks',
        label: 'Net FG Blk',
        hint: 'Own FG blocks minus opponent FG blocks. ±100 in luck score.',
        format: formatSignedStat,
      },
      {
        key: 'netLuckScore',
        label: 'Luck',
        hint: '100×net fumbles + 100×net FG blocks − 2×net tackles',
        format: formatSignedStat,
      },
    ],
  },
]

/**
 * Writes one figure the way its column asks for.
 *
 * @example
 * ```typescript
 * formatStatValue({ key: 'passerRating', label: 'Rating', format: v => v.toFixed(1) }, stats)
 * ```
 */
export function formatStatValue(column: StatColumn, stats: CoachStats): string {
  const value = Number(stats[column.key])
  return column.format ? column.format(value) : value.toLocaleString()
}

/* ---------------------------------------------- in-game player categories -- */

/**
 * One figure within an in-game player category.
 *
 * @remarks
 * Keyed over {@link RosterStatLine} rather than the whole
 * {@link RosterPlayerStats}, so the same column definition renders a season
 * total on the Leaderboards page and a single game's line on that player's game
 * log. Identity fields are excluded by construction, which is why they cannot
 * be named here by mistake.
 */
export interface RosterStatColumn {
  key: keyof RosterStatLine
  /** Column heading, kept short enough for a leaderboard header. */
  label: string
  /** Rendering for a figure that is not a plain count, e.g. a percentage. */
  format?: (value: number) => string
}

/**
 * One leaderboard of NES roster players.
 *
 * @remarks
 * The mirror of {@link StatCategory} over {@link RosterPlayerStats}. There is no
 * `requiresBoxScore` flag because every figure here comes from a box score;
 * a scope without one has no rows at all, which the page reports as such.
 */
export interface RosterStatCategory {
  slug: string
  title: string
  description: string
  columns: RosterStatColumn[]
  /**
   * Column the leaderboard sorts by on first render.
   *
   * @remarks
   * Always a volume figure, never a rate. One season of RTL is a handful of
   * games, so opening a board on passer rating or yards per carry would put a
   * quarterback who went 1-for-1 above everyone who actually played. The rate
   * columns are still there to sort by; ranking on them is then the visitor's
   * deliberate choice rather than the page's claim.
   */
  defaultSort: keyof RosterPlayerStats
  /**
   * Whether this player belongs on this board at all.
   *
   * @remarks
   * A box score lists every roster slot of both teams, so without this a
   * passing board would carry every lineman on every team used, at zero
   * across. Keyed on attempts rather than yards so that a back who carried
   * four times for no gain still appears — he did play.
   *
   * Doubles as the test for whether a category is worth a column group on one
   * player's game log: a running back's log has no business carrying eight
   * passing columns, and "would he appear on this board" is exactly that
   * question already answered.
   */
  qualifies: (row: RosterStatLine) => boolean
}

/**
 * Leaderboards for the in-game players, the NES athletes on the Tecmo rosters.
 *
 * @remarks
 * Deliberately not a superset of {@link STAT_CATEGORIES}: there is no Scoring
 * board, because points belong to a team rather than to one athlete, and no
 * Offense board, because a combined yardage total across passing, rushing and
 * receiving would rank quarterbacks against everybody else.
 *
 * Defense includes tackles from season 34 on. Earlier seasons stored the
 * field as zero, so they do not move the board.
 */
export const ROSTER_STAT_CATEGORIES: RosterStatCategory[] = [
  {
    slug: 'passing',
    title: 'Passing',
    description: 'Quarterback volume, efficiency, and passer rating.',
    defaultSort: 'passingYards',
    qualifies: row => row.passingAttempts > 0,
    columns: [
      { key: 'passingAttempts', label: 'Att' },
      { key: 'passingCompletions', label: 'Cmp' },
      { key: 'completionPercentage', label: 'Cmp%', format: value => `${value.toFixed(1)}%` },
      { key: 'passingYards', label: 'Yds' },
      { key: 'passingTouchdowns', label: 'TD' },
      { key: 'interceptionsThrown', label: 'INT' },
      { key: 'passerRating', label: 'Rating', format: value => value.toFixed(1) },
    ],
  },
  {
    slug: 'rushing',
    title: 'Rushing',
    description: 'Carries, yardage, and yards per carry.',
    defaultSort: 'rushingYards',
    qualifies: row => row.rushingAttempts > 0,
    columns: [
      { key: 'rushingAttempts', label: 'Att' },
      { key: 'rushingYards', label: 'Yds' },
      { key: 'yardsPerCarry', label: 'Y/C', format: value => value.toFixed(2) },
      { key: 'rushingTouchdowns', label: 'TD' },
    ],
  },
  {
    slug: 'receiving',
    title: 'Receiving',
    description: 'Catches, yardage, and yards per catch.',
    defaultSort: 'receivingYards',
    qualifies: row => row.receptions > 0,
    columns: [
      { key: 'receptions', label: 'Rec' },
      { key: 'receivingYards', label: 'Yds' },
      { key: 'yardsPerReception', label: 'Y/R', format: value => value.toFixed(2) },
      { key: 'receivingTouchdowns', label: 'TD' },
    ],
  },
  {
    slug: 'defense',
    title: 'Defense',
    description: 'Tackles, pressure, and takeaways. Tackles are from season 34 on.',
    defaultSort: 'tackles',
    qualifies: row => row.sacks > 0 || row.tackles > 0 || row.interceptions > 0,
    columns: [
      { key: 'tackles', label: 'Tackles' },
      { key: 'sacks', label: 'Sacks' },
      { key: 'interceptions', label: 'INT' },
      { key: 'interceptionYards', label: 'INT yds' },
      { key: 'interceptionTouchdowns', label: 'INT TD' },
    ],
  },
  {
    slug: 'kicking',
    title: 'Kicking',
    description: 'Field goals and extra points.',
    defaultSort: 'fieldGoalsMade',
    qualifies: row => row.fieldGoalAttempts > 0 || row.extraPointAttempts > 0,
    columns: [
      { key: 'fieldGoalsMade', label: 'FGM' },
      { key: 'fieldGoalAttempts', label: 'FGA' },
      { key: 'extraPointsMade', label: 'XPM' },
      { key: 'extraPointAttempts', label: 'XPA' },
    ],
  },
]

/**
 * Writes one in-game player figure the way its column asks for.
 *
 * @param stats - A season total or a single game's line; both are
 * {@link RosterStatLine}, which is what lets the leaderboard and the game log
 * write the same statistic identically.
 *
 * @example
 * ```typescript
 * formatRosterStatValue({ key: 'yardsPerCarry', label: 'Y/C', format: v => v.toFixed(2) }, row)
 * ```
 */
export function formatRosterStatValue(
  column: RosterStatColumn,
  stats: RosterStatLine
): string {
  const value = Number(stats[column.key])
  return column.format ? column.format(value) : value.toLocaleString()
}
